import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Image, UploadSimple, Heart, Clock, CheckCircle, X } from "@phosphor-icons/react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Gallery = () => {
  const { user, getAuthHeaders } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDesc, setUploadDesc] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewImage, setViewImage] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { fetchGallery(); }, []);

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/gallery`, {
        headers: getAuthHeaders(), withCredentials: true,
      });
      setPosts(res.data);
    } catch (err) {
      console.error("Erro:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Arquivo muito grande (máx 10MB)"); return; }
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) { toast.error("Selecione uma foto ou vídeo"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("title", uploadTitle);
      formData.append("description", uploadDesc);
      await axios.post(`${BACKEND_URL}/api/gallery`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      toast.success("Enviado! Aguardando aprovação do admin.");
      setShowUpload(false);
      setUploadTitle("");
      setUploadDesc("");
      setSelectedFile(null);
      setPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-[#115E59]">Carregando galeria...</div>;

  return (
    <div data-testid="gallery-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#042F2E] mb-1">Galeria</h2>
          <p className="text-[#115E59]">Fotos e vídeos da comunidade MARABU</p>
        </div>
        <Button
          onClick={() => setShowUpload(!showUpload)}
          className="bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full px-6"
          data-testid="upload-btn"
        >
          <UploadSimple size={20} weight="bold" className="mr-2" />
          Enviar
        </Button>
      </div>

      {/* Upload form */}
      <AnimatePresence>
        {showUpload && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-2xl border border-[#E4DFD3] p-6 mb-8 shadow-sm overflow-hidden"
            data-testid="upload-form"
          >
            <h3 className="text-lg font-bold text-[#042F2E] mb-4">Enviar Foto ou Vídeo</h3>
            <form onSubmit={handleUpload} className="space-y-4">
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-[#E4DFD3] rounded-xl p-8 text-center cursor-pointer hover:border-[#FF5A26] transition-colors"
              >
                {preview ? (
                  <div className="relative inline-block">
                    {selectedFile?.type?.startsWith("video") ? (
                      <video src={preview} className="max-h-48 rounded-lg mx-auto" controls />
                    ) : (
                      <img src={preview} alt="Preview" className="max-h-48 rounded-lg mx-auto" />
                    )}
                    <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setPreview(null); }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <Image size={48} weight="duotone" className="text-[#E4DFD3] mx-auto mb-3" />
                    <p className="text-[#115E59] font-semibold">Clique para selecionar</p>
                    <p className="text-xs text-[#115E59] mt-1">Fotos (JPG, PNG) ou Vídeos (MP4) até 10MB</p>
                  </>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*,video/*" onChange={handleFileChange} className="hidden" />

              <div>
                <Label className="text-sm font-bold text-[#115E59]">Título</Label>
                <Input value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="Ex: Treino de Futsal" className="rounded-xl border-[#E4DFD3]" required data-testid="gallery-title-input" />
              </div>
              <div>
                <Label className="text-sm font-bold text-[#115E59]">Descrição (opcional)</Label>
                <Input value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)}
                  placeholder="Conte mais sobre esta foto..." className="rounded-xl border-[#E4DFD3]" data-testid="gallery-desc-input" />
              </div>
              <div className="flex items-center gap-2 text-xs text-[#115E59] bg-[#F4F1EA] rounded-xl p-3">
                <Clock size={16} weight="fill" className="text-[#FF5A26]" />
                Sua postagem será revisada pelo admin antes de aparecer na galeria
              </div>
              <Button type="submit" disabled={uploading}
                className="w-full bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full py-5"
                data-testid="submit-gallery-btn">
                {uploading ? "Enviando..." : "Enviar para Aprovação"}
              </Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gallery grid */}
      {posts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[#E4DFD3]">
          <Image size={64} weight="duotone" className="text-[#E4DFD3] mx-auto mb-4" />
          <p className="text-xl text-[#115E59]">Galeria vazia</p>
          <p className="text-sm text-[#115E59] mt-2">Seja o primeiro a compartilhar um momento!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {posts.map((post, i) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.03 }}
              onClick={() => setViewImage(post)}
              className="relative group cursor-pointer rounded-2xl overflow-hidden border border-[#E4DFD3] shadow-sm aspect-square bg-[#F4F1EA]"
              data-testid={`gallery-post-${post.id}`}
            >
              {post.media_type === "video" ? (
                <video src={post.media_data} className="w-full h-full object-cover" />
              ) : (
                <img src={post.media_data} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <div>
                  <p className="text-white font-bold text-sm">{post.title}</p>
                  <p className="text-white/80 text-xs">{post.user_name}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {viewImage && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setViewImage(null)}
            data-testid="lightbox"
          >
            <button className="absolute top-4 right-4 text-white/80 hover:text-white z-10" onClick={() => setViewImage(null)}>
              <X size={32} weight="bold" />
            </button>
            <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} exit={{ scale: 0.8 }}
              className="max-w-4xl max-h-[85vh] w-full" onClick={(e) => e.stopPropagation()}>
              {viewImage.media_type === "video" ? (
                <video src={viewImage.media_data} controls className="w-full max-h-[70vh] rounded-2xl" />
              ) : (
                <img src={viewImage.media_data} alt={viewImage.title} className="w-full max-h-[70vh] object-contain rounded-2xl" />
              )}
              <div className="text-white mt-4">
                <h3 className="text-xl font-bold">{viewImage.title}</h3>
                {viewImage.description && <p className="text-white/80 mt-1">{viewImage.description}</p>}
                <p className="text-white/60 text-sm mt-2">
                  Por {viewImage.user_name} - {new Date(viewImage.created_at).toLocaleDateString("pt-BR")}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
